import type { MtoRun } from '@entities/mto';
import {
  fmtDateTime,
  isMtoRunActive,
  runStageLabel,
  statusLabel,
  statusTone,
} from '@entities/mto';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Progress } from '@shared/ui/Progress';
import {
  IconArrowLeft,
  IconDownload,
  IconRefresh,
  IconTrash,
} from '@shared/ui/icons';
import { VedomostFreshness } from '../../MtoVedomostFreshness';
import {
  Back,
  DownloadButton,
  ErrBox,
  HeadActions,
  HeadLeft,
  HeadRow,
  ProgressBlock,
  ProgressPercent,
  ProgressStage,
  ProgressTopRow,
  RunTitle,
  SubMeta,
} from '../styles';

interface RunDetailHeaderProps {
  run: MtoRun;
  title: string;
  backTo: string;
  countsLabel: string;
  isSuppliers: boolean;
  isVedomost: boolean;
  canWrite: boolean;
  actionError: string | null;
  xlsxLoading: boolean;
  memoLoading: boolean;
  retrying: boolean;
  deleting: boolean;
  onExcel: () => void;
  onMemo: () => void;
  onRetry: () => void;
  onDeleteClick: () => void;
  onNavigateReconcile: () => void;
}

export function RunDetailHeader({
  run,
  title,
  backTo,
  countsLabel,
  isSuppliers,
  isVedomost,
  canWrite,
  actionError,
  xlsxLoading,
  memoLoading,
  retrying,
  deleting,
  onExcel,
  onMemo,
  onRetry,
  onDeleteClick,
  onNavigateReconcile,
}: RunDetailHeaderProps) {
  const active = isMtoRunActive(run.status);

  return (
    <>
      <Back to={backTo}>
        <IconArrowLeft size={16} />
        <span>К списку обработок</span>
      </Back>

      <Card>
        <HeadRow>
          <HeadLeft>
            <RunTitle>{title}</RunTitle>
            <SubMeta>
              <Badge $tone={statusTone(run.status)}>{statusLabel(run.status)}</Badge>
              <span>создан {fmtDateTime(run.created_at)}</span>
              <span>{countsLabel}</span>
            </SubMeta>
          </HeadLeft>
          <HeadActions>
            {run.status === 'completed' && (
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                onClick={onExcel}
                disabled={xlsxLoading}
                title={
                  isSuppliers
                    ? 'Все КП обработки в одной таблице Excel: строки — позиции, столбцы — КП поставщиков'
                    : 'Выгрузить результат обработки в Excel'
                }
              >
                <IconDownload size={14} />
                {xlsxLoading ? 'Готовлю…' : isSuppliers ? 'Сводная таблица КП' : 'Скачать Excel'}
              </Button>
            )}
            {run.status === 'completed' && isVedomost && canWrite && (
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={onNavigateReconcile}
                title="Сверить эту ведомость с заявками на закупку"
              >
                Сверить с заявками
              </Button>
            )}
            {run.status === 'failed' && canWrite && (
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={onRetry}
                disabled={retrying}
              >
                <IconRefresh size={14} />
                {retrying ? 'Запуск…' : 'Повторить'}
              </Button>
            )}
            {run.status === 'completed' && (
              <DownloadButton
                type="button"
                onClick={onMemo}
                disabled={memoLoading}
                title="Сформировать проект служебной записки по находкам обработки"
              >
                <IconDownload size={14} />
                {memoLoading ? 'Готовлю…' : 'Служебная записка'}
              </DownloadButton>
            )}
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={onDeleteClick}
              disabled={!canWrite || deleting}
              title={!canWrite ? 'Только просмотр' : undefined}
            >
              <IconTrash size={15} />
              Удалить
            </Button>
          </HeadActions>
        </HeadRow>

        {active && (
          <ProgressBlock>
            <ProgressTopRow>
              <ProgressStage>
                {runStageLabel(run.status, run.progress_percent, run.module)}
              </ProgressStage>
              <ProgressPercent>{Math.round(run.progress_percent)}%</ProgressPercent>
            </ProgressTopRow>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(run.progress_percent)}
              aria-label={`Прогресс обработки: ${runStageLabel(run.status, run.progress_percent, run.module)}`}
            >
              <Progress percent={run.progress_percent} />
            </div>
          </ProgressBlock>
        )}

        {run.status === 'failed' && run.error_message && <ErrBox>{run.error_message}</ErrBox>}
        {actionError && <ErrBox role="alert">{actionError}</ErrBox>}
        {isVedomost && run.status === 'completed' && (
          <VedomostFreshness run={run} canWrite={canWrite} />
        )}
      </Card>
    </>
  );
}
